import React from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { media } from "../theme/breakpoints";
import { fmtINR } from "../utils/formatters";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCTAButton,
} from "../../common/Buttons";
import { DeleteBtn } from "../../internal/NoticeGeneration/component/HeaderFooter";
import { CustomInputField } from "../../common/FormInputs";

const ChargesStage = ({
  onsiteItems = [],
  lineItems,
  customLines,
  setLineItems,
  setCustomLines,
  computedTotal,
  onBack,
  onNext,
}) => {
  const byCategory = {};
  onsiteItems.forEach((it) => {
    if (!byCategory[it.category]) byCategory[it.category] = [];
    byCategory[it.category].push(it);
  });

  const setQty = (id, value) => {
    const qty = parseFloat(value) || 0; // Fixed: Proper number conversion
    setLineItems((prev) => {
      const next = { ...prev };
      if (qty <= 0) {
        delete next[id];
      } else {
        next[id] = qty;
      }
      return next;
    });
  };

  return (
    <Wrap>
      <Notice>
        Enter quantities for items actually consumed. Leave blank if not used.
      </Notice>

      {onsiteItems.length === 0 && (
        <EmptyNote>
          No on-site charge items are configured. Use "Other Charges" below.
        </EmptyNote>
      )}

      {Object.entries(byCategory).map(([cat, items]) => (
        <CategoryBlock key={cat}>
          <CategoryTitle>{cat}</CategoryTitle>
          {items.map((item) => {
            const qty = lineItems[item.id] || 0;
            const isAmountEntry = item.amountEntry;

            return (
              <ItemRow className="bundle-doc" key={item.id}>
                <ItemMeta>
                  <ItemLabel>{item.label}</ItemLabel>
                  {item.note && <ItemNote>{item.note}</ItemNote>}
                </ItemMeta>

                <Price>
                  {isAmountEntry
                    ? "Enter Amount"
                    : item.unit
                      ? `${fmtINR(item.price || 0)} / ${item.unit}`
                      : fmtINR(item.price || 0)}
                </Price>

                <CustomInputField
                  variant="outlined"
                  $isAmount={isAmountEntry}
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  value={qty || ""}
                  onChange={(e) => setQty(item.id, e.target.value)}
                  placeholder="0"
                />

                <LineTotal $hasValue={qty > 0}>
                  {qty > 0
                    ? fmtINR(isAmountEntry ? qty : qty * (item.price || 0))
                    : "—"}
                </LineTotal>
              </ItemRow>
            );
          })}
        </CategoryBlock>
      ))}

      <CustomBlock>
        <CustomHeader>
          <SectionTitle>Other Charges</SectionTitle>
          <AddLineBtn
            onClick={() =>
              setCustomLines([...customLines, { desc: "", amount: 0 }])
            }
          >
            + Add Line
          </AddLineBtn>
        </CustomHeader>

        {customLines.length === 0 ? (
          <div
            style={{
              fontSize: "13px",
              color: "#94a3b8",
              fontStyle: "italic",
              padding: "12px 0",
            }}
          >
            No additional charges. Click "+ Add Line" if needed.
          </div>
        ) : (
          customLines.map((cl, i) => (
            <CustomRow key={i} className="paymentHistory">
              <CustomInputField
                variant="outlined"
                type="text"
                value={cl.desc}
                onChange={(e) => {
                  const n = [...customLines];
                  n[i].desc = e.target.value;
                  setCustomLines(n);
                }}
                placeholder="Description (e.g. Extra printing)"
              />
              <CustomInputField
                variant="outlined"
                type="number"
                onWheel={(e) => e.target.blur()}
                value={cl.amount || ""}
                onChange={(e) => {
                  const n = [...customLines];
                  n[i].amount = parseFloat(e.target.value) || 0;
                  setCustomLines(n);
                }}
                placeholder="₹ Amount"
              />
              <DeleteBtn
                onClick={() =>
                  setCustomLines(customLines.filter((_, j) => j !== i))
                }
              >
                <img
                  src={require("../../../assets/images/removeIcon.svg")}
                  alt="delete"
                />
              </DeleteBtn>
            </CustomRow>
          ))
        )}
      </CustomBlock>

      <TotalBar>
        <TotalLabel>Amount Due On-site</TotalLabel>
        <TotalValue>{fmtINR(computedTotal)}</TotalValue>
      </TotalBar>

      <FooterRow>
        <PrimaryOutlinedCTAButton
          style={{ width: "110px", padding: "12px 12px", borderRadius: 8 }}
          variant="secondary"
          onClick={onBack}
        >
          Back
        </PrimaryOutlinedCTAButton>
        <PrimaryCTAButton
          style={{ width: "auto", padding: "12px 12px", borderRadius: 8 }}
          variant="primary"
          onClick={onNext}
          disabled={computedTotal === 0}
        >
          Continue to Payment
        </PrimaryCTAButton>
      </FooterRow>
    </Wrap>
  );
};

export default ChargesStage;

const Wrap = styled.div`
  padding: 24px;
  max-width: 960px;
  margin: 0 auto;
`;

const Notice = styled.div`
  background: ${COLORS.NOTIFICATION_BLUE};
  border: 1px solid #b8cee8;
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 28px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  color: ${COLORS.COLOR_DARK};
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const EmptyNote = styled.div`
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
  margin-bottom: 28px;
`;

const CategoryBlock = styled.div`
  margin-bottom: 32px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid ${COLORS.HEADER_BORDER};
`;

const CategoryTitle = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 13px;
  font-weight: 800;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${COLORS.HEADER_BORDER};
`;

const ItemRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px 16px;
  padding: 16px 0;
  align-items: center;
  border-bottom: 1px solid ${COLORS.HEADER_BORDER};

  &:last-child {
    border-bottom: none;
  }

  ${media.mobile} {
    grid-template-columns: 2.2fr 100px 110px 110px;
    gap: 16px;
  }
`;

const ItemMeta = styled.div``;

const ItemLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 15px;
  font-weight: 600;
  color: ${COLORS.COLOR_DARK};
  margin-bottom: 4px;
`;

const ItemNote = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12.5px;
  color: #8e99a8;
  line-height: 1.4;
`;

const Price = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  color: #64748b;
  font-weight: 500;
  text-align: right;

  ${media.mobile} {
    text-align: center;
  }
`;

const LineTotal = styled.div`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 15px;
  font-weight: 700;
  color: ${({ $hasValue }) => ($hasValue ? COLORS.BTN_GREEN : "#94a3b8")};
  text-align: right;
  min-width: 100px;
`;

const CustomBlock = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid ${COLORS.HEADER_BORDER};
  margin-bottom: 28px;
`;

const CustomHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${COLORS.HEADER_BORDER};
`;

const SectionTitle = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 13px;
  font-weight: 800;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const AddLineBtn = styled.button`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 13px;
  font-weight: 700;
  color: ${COLORS.BTN_GREEN};
  background: rgba(0, 131, 140, 0.08);
  padding: 8px 16px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 131, 140, 0.15);
    transform: translateY(-1px);
  }
`;

const CustomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 48px;
  gap: 12px;
  align-items: center;
  padding: 10px 0;
`;

const TotalBar = styled.div`
  background: linear-gradient(135deg, #1e2937, #334155);
  color: white;
  border-radius: 20px;
  padding: 24px 28px;
  margin-bottom: 28px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.3);
`;

const TotalLabel = styled.span`
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  opacity: 0.9;
`;

const TotalValue = styled.span`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 28px;
  font-weight: 800;
  color: #fbbf24;
  letter-spacing: -0.02em;
`;

const FooterRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;
